import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';

import Slider, { Settings } from 'react-slick';
import styled from 'styled-components';
import { useEffect, useState } from 'react';
import { proxyGet } from '../../api/ManApi';
import {
  getProviderIcon,
  IProviderDocumentViewObj,
  IProviderItemView,
} from '../../store/providers/providerTypes';
import { black } from '../../utils/style/colors';
import { getDomainAndPort, getProtocolDomainAndPort } from '../../api/Utils';
import Modal from '../../components/Modal/Modal';
import Icon from '../../components/Icon/Icon';
import { CarouselContainer } from '../HomePage/styles';
import { replaceAbsoluteUrls } from 'components/GuideModal/GuideModal';
import banner from '../../assets/images/new-features/banner.png';
import { IThisUserState } from '../../store/thisUser/thisUserSlice';

const Container = styled.div`
  height: 100% !important;
`;

const SliderItem = styled.a`
  display: flex !important;
  flex-direction: row !important;
  flex-wrap: nowrap !important;
  height: 100% !important;
  color: ${black};
  direction: rtl;
  padding: ${({ padding }) => padding || '0 30px'};
  //margin: 0 30px;
  &:hover {
    cursor: pointer;
  }
`;

const ContentContainer = styled.div`
  flex: 1;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  font-weight: bold;
  font-size: 1.2rem;
`;

const ImageHolder = styled.div`
  flex-basis: 9rem;
  display: flex;
  padding: 0.5rem;
  justify-content: center;
  align-items: center;
  height: 100%;
`;

const Image = styled.img`
  object-fit: contain;
  height: 7rem;
`;

const ImageContainer = styled.div`
  font-size: 6rem;
  display: flex;
  justify-content: center;
  align-items: center;
`;

interface IProps {
  folderId: number;
  onShowNewVersionModal: () => void;
  thisUser: IThisUserState;
  onDismissModal: (itemId: string) => void;
}

export const getProxiedUrl = (url: string) => {
  const baseUrl = getProtocolDomainAndPort(getDomainAndPort());
  return `${baseUrl}/man/rest/proxy/get?url=${url}`;
};

const Carousel: React.FC<IProps> = ({
  folderId,
  onShowNewVersionModal,
  thisUser,
  onDismissModal,
}) => {
  const [activeModalContent, setActiveModalContent] = useState<string>('');
  const [isModalVisible, setModalVisible] = useState(false);
  const [selectedModalHeader, setSelectedModalHeader] = useState<string>('');
  const [documentItems, setDocumentItems] = useState([] as IProviderItemView[]);

  useEffect(() => {
    proxyGet(
      `https://app.rastech.ir/man/rest/provider/getDocumentsInFolder?providerDocumentId=${folderId}&noCache=true`
    ).then((res) => {
      setDocumentItems(
        res.data.filter(
          (item: IProviderItemView) =>
            item.status === 'exists' &&
            (!item.tags ||
              !item.tags.length ||
              item.tags.find((it) => it.text === window.location.hostname) ||
              (item.tags.find((it) => it.text === 'all_domains') &&
                !item.tags.find(
                  (it) => it.text === 'skip_' + window.location.hostname
                )))
        )
      );
    });
  }, [folderId]);

  const settings: Settings = {
    dots: true,
    infinite: documentItems.length > 1,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 3000,
    pauseOnHover: true,
    rtl: false,
  };

  useEffect(() => {
    const autoShow = documentItems.find((it) => {
      return (
        !!it.tags.find((it) => it.text === 'conf_popup') &&
        thisUser.userFlags?.['homepage']?.find(
          (el) => el.flagName === `homeCarousel-${it.id}`
        )?.flagValue !== 'true'
      );
    });
    if (autoShow && !isModalVisible) {
      handleOpenModal(null, autoShow.id.toString());
      setSelectedModalHeader(autoShow.name || '');
    }
  }, [documentItems]);

  const domain = getDomainAndPort().domain;
  const [openItemId, setOpenItemId] = useState<string>();
  const handleOpenModal = (e: Event | null, id: string) => {
    e?.preventDefault();
    proxyGet(
      `https://app.rastech.ir/man/rest/provider/getDocument?providerDocumentId=${id}`
    ).then((res) => {
      const item: IProviderDocumentViewObj = res.data;
      const contentUrl = item.document.url;
      proxyGet(contentUrl).then((contentRes) => {
        if (item.tags.find((it) => it.text === 'conf_noProxy'))
          setActiveModalContent(contentRes.data);
        else setActiveModalContent(replaceAbsoluteUrls(contentRes.data));
      });
    });
    setModalVisible(true);
    setOpenItemId(id);
  };

  const ItemContent: React.FC<{
    name: string;
    thumbnailUrl?: string;
    type: string;
  }> = ({ name, thumbnailUrl, type }) => (
    <>
      <ImageHolder>
        {thumbnailUrl ? (
          <Image src={getProxiedUrl(thumbnailUrl)} />
        ) : (
          <ImageContainer>
            <Icon type={getProviderIcon[type]} />
          </ImageContainer>
        )}
      </ImageHolder>
      <ContentContainer>{name}</ContentContainer>
    </>
  );

  if (!documentItems.length) return <></>;
  return (
    <CarouselContainer>
      {isModalVisible && (
        <Modal
          width="75%"
          isActive={isModalVisible}
          title={selectedModalHeader}
          onClose={() => {
            setActiveModalContent('');
            setModalVisible(false);
            openItemId && onDismissModal(`homeCarousel-${openItemId}`);
          }}
        >
          <div
            className="ck-view text-black"
            dangerouslySetInnerHTML={{ __html: activeModalContent || '' }}
          />
        </Modal>
      )}
      <Container className="slider-container">
        <Slider {...settings}>
          {documentItems.map((item: IProviderItemView) =>
            item.type === 'document' ? (
              <SliderItem
                key={item.id.toString()}
                onClick={(event: Event) => {
                  setSelectedModalHeader(item.name || '');
                  handleOpenModal(event, item.id.toString());
                }}
              >
                <ItemContent
                  name={item.name || ''}
                  thumbnailUrl={item.thumbnailUrl}
                  type={item.type}
                />
              </SliderItem>
            ) : (
              <SliderItem
                key={item.id.toString()}
                href={
                  item.type === 'folder'
                    ? `https://${domain}/content/${item.providerId}/f/${item.id}`
                    : `https://${domain}/content/${item.providerId}/direct/${item.id}`
                }
                target="_blank"
              >
                <ItemContent
                  name={item.name || ''}
                  thumbnailUrl={item.thumbnailUrl}
                  type={item.type}
                />
              </SliderItem>
            )
          )}
          <SliderItem
            padding={'0'}
            style={{
              paddingLeft: '0',
              paddingRight: '0',
            }}
            key={'newFeatures2'}
            onClick={() => {
              onShowNewVersionModal();
            }}
          >
            <Image style={{ width: '100%', height: '100%' }} src={banner} />
          </SliderItem>
        </Slider>
      </Container>
    </CarouselContainer>
  );
};

export default Carousel;
